<script setup>
// Shared dialog shell for the redesign (hairline card + header with close +
// body + footer). Use for bespoke (non-CRUD) dialogs so the card/header/footer
// styling lives in one place instead of being copy-pasted per page.
// CRUD forms should keep using <crud-form-dialog>; destructive confirms should
// use <crud-confirm-dialog>.
//
//   <tl-dialog v-model="open" title="Rename team" :max-width="360">
//     <template #head-actions><v-btn ...>Add</v-btn></template>   (optional, sits left of ✕)
//     ...body...
//     <template #actions><v-btn ...>Done</v-btn></template>
//   </tl-dialog>
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: null
  },
  maxWidth: {
    type: [String, Number],
    default: 420
  },
  // Edge-to-edge body (lists whose rows carry their own padding/dividers).
  flush: {
    type: Boolean,
    default: false
  },
  // Long editing jobs: Esc and clicks outside don't close. The ✕ still emits
  // update:modelValue(false), so the parent can ask "Discard your changes?".
  persistent: {
    type: Boolean,
    default: false
  },
  // A persistent dialog that Escape still closes, exactly like the ✕ (emits
  // update:modelValue(false)). For dialogs where closing has a meaning of its
  // own (the competition questions: closing is a no), keyboard users need the
  // same way out as the ✕, while a stray click outside still does nothing.
  closeOnEscape: {
    type: Boolean,
    default: false
  },
  // Tooltip on the ✕ (say what closing does, e.g. "Cancel rollover").
  closeLabel: {
    type: String,
    default: 'Close'
  }
});
const emit = defineEmits(['update:modelValue']);

// Vuetify's overlay re-emits Escape as `keydown` whether focus is inside the
// dialog or not; a persistent one then only bounces, so the close is ours.
const onKeydown = (e) => {
  if (props.closeOnEscape && e?.key === 'Escape') emit('update:modelValue', false);
};
</script>

<template>
  <v-dialog
      :model-value="modelValue"
      :max-width="maxWidth"
      :persistent="persistent"
      @update:model-value="emit('update:modelValue', $event)"
      @keydown="onKeydown"
  >
    <div class="tl-dialog">
      <div v-if="title || $slots.title" class="tl-dialog__head">
        <span class="tl-dialog__title"><slot name="title">{{ title }}</slot></span>
        <!-- optional actions in the header, just left of the close ✕ -->
        <slot name="head-actions"/>
        <v-tooltip :text="closeLabel" location="top">
          <template #activator="{props: tip}">
            <v-btn
                v-bind="tip"
                icon="mdi-close"
                variant="text"
                size="small"
                color="charcoal"
                :aria-label="closeLabel"
                @click="emit('update:modelValue', false)"
            />
          </template>
        </v-tooltip>
      </div>

      <div class="tl-dialog__body" :class="{'tl-dialog__body--flush': flush}">
        <slot/>
      </div>

      <div v-if="$slots.actions" class="tl-dialog__foot">
        <slot name="actions"/>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped lang="scss">
.tl-dialog {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  overflow: hidden;
  // Never taller than the viewport: head/foot stay pinned and the body
  // scrolls (tall content like the Stripe Payment Element).
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 48px);
}

.tl-dialog__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  flex-shrink: 0;
}

.tl-dialog__title {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.tl-dialog__body {
  padding: 16px;
  overflow-y: auto;
  flex: 1 1 auto;

  &--flush {
    padding: 0;
  }
}

.tl-dialog__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  flex-shrink: 0;
  /* The row must WRAP. Without this, two buttons wider than the dialog are laid
     out from the right edge and the first one is pushed off the LEFT of the
     screen — measured at 400px on the combine-accounts dialog, where "Keep them
     separate" started at x = -1 and was unreadable and unreachable. Nothing
     clipped the row, so there was no scrollbar to hint at it either. */
  flex-wrap: wrap;
}
</style>
